import type { Coordinates } from '@/types';

/**
 * Shareable-location URL scheme (pure helpers — no React, no side effects).
 *
 * Two complementary forms, both decoded on load and either produced by the
 * Share control:
 *
 *  1. VIEW hash — mirrors the current map viewport, kept in sync as the user
 *     pans/zooms (OSM-style):
 *
 *         #map=<zoom>/<lat>/<lng>
 *         e.g.  #map=16/17.385040/78.486671
 *
 *  2. PIN query — a specific point to drop a marker on and highlight in a
 *     "shared location" card:
 *
 *         ?ll=<lat>,<lng>
 *         e.g.  ?ll=17.385040,78.486671
 *
 * A pin link also carries a view hash so the recipient lands at a sensible
 * zoom, e.g.:
 *
 *     https://tensormaps.com/?ll=17.385040,78.486671#map=17/17.385040/78.486671
 *
 * Coordinates are rounded to 6 decimals (~0.1 m) — enough to point at a
 * doorway without leaking excess precision or bloating the link.
 */

const COORD_DECIMALS = 6;
const ZOOM_DECIMALS = 2;
// Zoom used for a shared pin when the sharer's own zoom is unknown.
export const DEFAULT_PIN_ZOOM = 17;

export interface SharedView {
  center: Coordinates;
  zoom: number | null;
}

export interface ParsedShareLocation {
  view: SharedView | null;
  pin: Coordinates | null;
}

function roundCoord(n: number): number {
  return Number(n.toFixed(COORD_DECIMALS));
}

function isValidLat(lat: number): boolean {
  return Number.isFinite(lat) && lat >= -90 && lat <= 90;
}

function isValidLng(lng: number): boolean {
  return Number.isFinite(lng) && lng >= -180 && lng <= 180;
}

function isValidZoom(zoom: number): boolean {
  return Number.isFinite(zoom) && zoom >= 0 && zoom <= 24;
}

/**
 * Parse the location out of a URL's hash + query string.
 * Accepts the raw `window.location.hash` (with or without a leading '#') and
 * `window.location.search` (with or without a leading '?'). Never throws.
 */
export function parseShareLocation(hash: string, search: string): ParsedShareLocation {
  return {
    view: parseViewHash(hash),
    pin: parsePinQuery(search),
  };
}

function parseViewHash(hash: string): SharedView | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw) return null;
  // Support the hash appearing among other params: `foo=bar&map=16/1/2`
  const params = new URLSearchParams(raw);
  const map = params.get('map');
  if (!map) return null;

  const parts = map.split('/');
  if (parts.length !== 3) return null;
  const zoom = Number(parts[0]);
  const lat = Number(parts[1]);
  const lng = Number(parts[2]);
  if (!isValidLat(lat) || !isValidLng(lng)) return null;

  return {
    center: { lat, lng },
    zoom: isValidZoom(zoom) ? zoom : null,
  };
}

function parsePinQuery(search: string): Coordinates | null {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  if (!raw) return null;
  const params = new URLSearchParams(raw);
  const ll = params.get('ll');
  if (!ll) return null;

  const [latStr, lngStr] = ll.split(',');
  const lat = Number(latStr);
  const lng = Number(lngStr);
  if (!isValidLat(lat) || !isValidLng(lng)) return null;

  return { lat, lng };
}

/** Build the `#map=<zoom>/<lat>/<lng>` hash fragment (includes the leading '#'). */
export function formatViewHash(zoom: number, center: Coordinates): string {
  const z = Number(zoom.toFixed(ZOOM_DECIMALS));
  return `#map=${z}/${roundCoord(center.lat)}/${roundCoord(center.lng)}`;
}

/** Full shareable link for the current viewport (view hash only, no pin). */
export function buildViewLink(zoom: number, center: Coordinates): string {
  return `${window.location.origin}${window.location.pathname}${formatViewHash(zoom, center)}`;
}

/**
 * Full shareable link for a specific point: a `?ll=` pin plus a view hash so the
 * recipient lands zoomed-in on it.
 */
export function buildPinLink(coordinates: Coordinates, zoom?: number): string {
  const z = zoom != null && isValidZoom(zoom) ? zoom : DEFAULT_PIN_ZOOM;
  const ll = `${roundCoord(coordinates.lat)},${roundCoord(coordinates.lng)}`;
  return `${window.location.origin}${window.location.pathname}?ll=${ll}${formatViewHash(z, coordinates)}`;
}
